/**
 * Sweep Control — one sweep-run row, expandable to its detail. Renders exactly
 * what get_sweep_health returns; the classification is shown as Acceptable /
 * Needs attention / Skipped, and the unresolved worklist, collector errors, and
 * ingest error body are surfaced honestly (no suppression, no editorializing).
 */

import { useState } from "react";
import { classificationLabel } from "../logic";
import type { RawSweepRun } from "../types";
import { ErrorBody } from "./ErrorBody";
import { IconChevron } from "./icons";

function dateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Honest one-line rendering of an unknown worklist / error entry. */
function lineText(item: unknown): string {
  if (item && typeof item === "object" && !Array.isArray(item)) {
    const o = item as Record<string, unknown>;
    const name = typeof o.organization_name === "string" ? o.organization_name : null;
    const source = typeof o.signal_source === "string" ? o.signal_source : null;
    if (name || source) return [name, source].filter(Boolean).join(" · ");
  }
  if (typeof item === "string") return item;
  try {
    return JSON.stringify(item);
  } catch {
    return String(item);
  }
}

function chipClass(classification: string): string {
  switch (classification) {
    case "acceptable":
      return "sxsw-chip--ok";
    case "unacceptable":
      return "sxsw-chip--warn";
    default:
      return "sxsw-chip--muted";
  }
}

function List({ label, items }: { label: string; items: unknown[] }): JSX.Element | null {
  if (!items || items.length === 0) return null;
  return (
    <div className="sxsw-detail-block">
      <p className="sxsw-detail-label">
        {label} <span className="sxsw-detail-count">{items.length}</span>
      </p>
      <ul className="sxsw-detail-list">
        {items.map((item, i) => (
          <li key={i}>{lineText(item)}</li>
        ))}
      </ul>
    </div>
  );
}

export function HealthRow({ run }: { run: RawSweepRun }): JSX.Element {
  const [open, setOpen] = useState(false);
  // An in-flight run (reserved row, status "started") has no meaningful
  // classification yet — the backend fall-through would read "acceptable". Show
  // a neutral, status-derived "Running" chip instead of a premature success
  // verdict; never fabricated.
  const inFlight = run.status === "started";

  return (
    <li className="sxsw-run">
      <button
        type="button"
        className="sxsw-run-summary"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="sxsw-run-chevron">
          <IconChevron open={open} />
        </span>
        <span className="sxsw-run-when">{dateTime(run.startedAt)}</span>
        <span className={`sxsw-chip ${inFlight ? "sxsw-chip--muted" : chipClass(run.classification)}`}>
          {inFlight ? "Running" : classificationLabel(run.classification)}
        </span>
        <span className="sxsw-run-meta">
          {run.status} · {run.ingestedCount} ingested
          {run.triggerSource.type ? ` · ${run.triggerSource.type}` : ""}
        </span>
      </button>

      {open && (
        <div className="sxsw-run-detail">
          <dl className="sxsw-facts">
            <div><dt>Started</dt><dd>{dateTime(run.startedAt)}</dd></div>
            <div><dt>Finished</dt><dd>{dateTime(run.finishedAt)}</dd></div>
            <div><dt>Ingested</dt><dd>{run.ingestedCount}</dd></div>
            <div><dt>Unresolved recorded</dt><dd>{run.unresolvedRecordedCount}</dd></div>
            {run.ingestHttpStatus != null && (
              <div><dt>Ingest HTTP</dt><dd>{run.ingestHttpStatus}</dd></div>
            )}
            {run.triggerSource.source && (
              <div><dt>Source</dt><dd>{run.triggerSource.source}</dd></div>
            )}
          </dl>

          {run.reasons.length > 0 && (
            <div className="sxsw-reasons">
              {run.reasons.map((reason) => (
                <span key={reason} className="sxsw-reason">{reason}</span>
              ))}
            </div>
          )}

          <List label="Unresolved worklist" items={run.unresolvedWorklist} />
          <List label="Collector errors" items={run.collectorErrors} />
          <List label="Collector skipped" items={run.collectorSkipped} />

          <ErrorBody label="Ingest response body" body={run.ingestResponseBody} />
        </div>
      )}
    </li>
  );
}
